// Asset schema + built-in registry for the virtual cornhole game.
//
// Everything that appears in the 3D scene (scenes, boards, bags, arms,
// players, ghosts) is a discrete data-described asset with a stable id.
// The renderer takes a GameLoadout (a set of asset ids — one per slot)
// plus an AssetRegistry (the catalog of what's available) and builds
// the scene from those.
//
// This file owns the schema and the built-in catalog. Adding a new
// board / bag / ghost / scene is a single entry in BUILT_IN_REGISTRY
// below — no renderer changes required as long as the new asset's
// data fields stay within the existing schema. Custom builder hooks
// (for assets with bespoke geometry) can be added per-kind later
// without breaking the data path.

export type AssetKind =
  | "scene"
  | "board"
  | "bag"
  | "player"
  | "ghost";

export interface AssetBase {
  id: string;
  kind: AssetKind;
  name: string;
  description?: string;
}

// ----- Scene -----

export type GroundShape = "grass" | "asphalt" | "neon";

export interface SceneAsset extends AssetBase {
  kind: "scene";
  palette: {
    zenith: number;
    horizon: number;
    groundBase: number;
    groundAccent: number;
    groundDirt: number;
    groundShape: GroundShape;
    sun: number;
    skyLight: number;
    groundLight: number;
  };
  // Optional photographic 2:1 panoramic backdrop. When set the
  // renderer paints this on a flat backdrop plane behind the boards
  // (NOT equirect mapping — that distorts non-panoramic photos).
  // Built-ins leave it unset and fall back to the procedural sky;
  // admin-generated scenes carry an R2 URL from the asset designer.
  skyImage?: string;
  // Per-scene BACKDROP plane transform. Each generated scene gets
  // its own framing (distance / size / texture pan + zoom) because
  // the right framing for a panoramic backyard is wildly different
  // from a portrait neon arena. Saved to scene.metadata via the
  // Align Scene modal in /admin/design; renderer reads from here
  // (no global fallback — when unset the renderer uses hardcoded
  // sane defaults). Identical knobs to what used to live on
  // PhysicsTuning before they moved per-asset.
  backdropDistanceZ?: number;
  backdropOffsetX?: number;
  backdropOffsetY?: number;
  backdropWidth?: number;
  backdropHeight?: number;
  backdropScale?: number;
  backdropOffsetU?: number;
  backdropOffsetV?: number;
}

// ----- Board -----

export interface BoardAsset extends AssetBase {
  kind: "board";
  // Dimensions (meters).
  width: number;
  length: number;
  thickness: number;
  holeRadius: number;
  holeFromBack: number;
  // Pose.
  rakeRise: number;
  frontLift: number;
  // Materials (hex colors). The procedural wood texture is tinted by `wood`.
  wood: number;
  stripe: number;
  legWood: number;
  // Optional top-deck image. When set, the deck face uses this URL as a
  // photographic / illustrated texture instead of the procedural wood
  // grain (legs + sides still take `legWood` / `wood`). Either a
  // bundled Vite-imported URL or a /generated/... path from the admin
  // designer.
  topImage?: string;
}

// ----- Bag -----

export interface BagAsset extends AssetBase {
  kind: "bag";
  color: number;
  size: number; // edge length, meters
  thickness: number;
  roughness: number;
  // Optional top/bottom face image. When set, both the top and bottom
  // faces of the cloth-bag silhouette use this URL as a photo /
  // illustration (sides stay solid-color cloth). Either a bundled
  // Vite-imported URL or a /generated/bags/... path from the admin
  // bag designer.
  topImage?: string;
  // Per-bag texture transform — baked into the asset so every render
  // path (player finger bag, pile, in-flight, rig-held) shows the
  // image with the same framing. Edited in /admin/design's bag
  // alignment tool; defaults give the raw 1:1 wrap.
  //   texOffsetU/V: -0.5..0.5, shifts texture left/right (U) or
  //                  up/down (V) on the cap face.
  //   texScale:      0.3..3, zoom (1 = fill the cap exactly).
  //   texRotationDeg: -180..180, rotation around the cap center.
  texOffsetU?: number;
  texOffsetV?: number;
  texScale?: number;
  texRotationDeg?: number;
  // Per-bag horizontal / vertical mirror toggles. Useful when the
  // texture's logo / text reads correctly in the alignment modal
  // but appears flipped on the 3D bag (which can happen depending
  // on how the source image was authored and how the bag mesh's
  // UV remap ends up oriented). Defaults to false = current
  // un-flipped behavior.
  texFlipU?: boolean;
  texFlipV?: boolean;
}

// ----- Player + Ghost -----

export interface PlayerAsset extends AssetBase {
  kind: "player";
  bagId: string;
}

export interface GhostAsset extends AssetBase {
  kind: "ghost";
  // Default bag color for this opponent. Bag actually thrown comes
  // from the loadout (per-team picker); this is just the asset
  // default fallback.
  bagId: string;
  // AI bias: lower aim sigma + tighter power jitter = more accurate.
  // Targets ~averagePointsPerRound across many rounds when the player
  // contributes nothing.
  aimSigma: number;
  powerMs: number;
  powerJitter: number;
  loft: number;
  averagePointsPerRound: number;
  tagline: string;
}

// ----- Registry -----

export interface AssetRegistry {
  scenes: Record<string, SceneAsset>;
  boards: Record<string, BoardAsset>;
  bags: Record<string, BagAsset>;
  players: Record<string, PlayerAsset>;
  ghosts: Record<string, GhostAsset>;
}

// ----- Loadout -----

// A loadout fully specifies what the renderer should draw + what AI to
// run. Each team picks a bag (defaults come from the player / ghost
// preferences) so future "skins" land naturally.
export interface TeamLoadout {
  // Exactly one of (playerId, ghostId) — human seat vs. AI seat.
  playerId?: string;
  ghostId?: string;
  bagId: string;
}

export interface GameLoadout {
  sceneId: string;
  boardId: string;
  team1: TeamLoadout;
  team2: TeamLoadout;
}

// ----- Built-in catalog -----

const SCENES: Record<string, SceneAsset> = {
  backyard: {
    id: "backyard",
    kind: "scene",
    name: "Backyard",
    description: "Sunny lawn, blue sky, hose-and-grill energy.",
    palette: {
      zenith: 0x6ea9e0,
      horizon: 0xdde7eb,
      groundBase: 0x4e7e2d,
      groundAccent: 0x6da93d,
      groundDirt: 0x3a2a14,
      groundShape: "grass",
      sun: 0xfff2d8,
      skyLight: 0xa6cdf4,
      groundLight: 0x4e6a30,
    },
  },
  "neon-arena": {
    id: "neon-arena",
    kind: "scene",
    name: "Neon Arena",
    description: "Late-night vapor venue. Synth-haze and grid floors.",
    palette: {
      zenith: 0x05030f,
      horizon: 0x261050,
      groundBase: 0x111122,
      groundAccent: 0x2a1a4a,
      groundDirt: 0x0a0510,
      groundShape: "neon",
      sun: 0xff80e0,
      skyLight: 0x4020a0,
      groundLight: 0x150828,
    },
  },
  tailgate: {
    id: "tailgate",
    kind: "scene",
    name: "Tailgate",
    description: "Stadium parking lot, blue-sky pre-game.",
    palette: {
      zenith: 0xc78758,
      horizon: 0xeab676,
      groundBase: 0x3a3a3a,
      groundAccent: 0x555555,
      groundDirt: 0x252525,
      groundShape: "asphalt",
      sun: 0xffd098,
      skyLight: 0xeab676,
      groundLight: 0x402810,
    },
  },
  "park-shade": {
    id: "park-shade",
    kind: "scene",
    name: "Park, shade",
    description: "Cooler park lawn. Hazy overcast, deep-green turf.",
    palette: {
      zenith: 0x88a5c0,
      horizon: 0xc8d4dc,
      groundBase: 0x355e22,
      groundAccent: 0x4a7a32,
      groundDirt: 0x2a1f10,
      groundShape: "grass",
      sun: 0xeef2f8,
      skyLight: 0xb8c8d8,
      groundLight: 0x2e4218,
    },
  },
  "dusk-yard": {
    id: "dusk-yard",
    kind: "scene",
    name: "Dusk yard",
    description: "Golden-hour backyard. Warm raking light.",
    palette: {
      zenith: 0x3a3868,
      horizon: 0xfaa14d,
      groundBase: 0x4a6b2c,
      groundAccent: 0x6e8f3a,
      groundDirt: 0x352510,
      groundShape: "grass",
      sun: 0xffb070,
      skyLight: 0xffa86e,
      groundLight: 0x5a4022,
    },
  },
};

// All boards share regulation geometry — only the surface treatment
// (wood tint, stripe, leg color, optional photographic deck) varies.
// Spread the base into each preset so the geometry stays in lock-step.
const BOARD_GEOMETRY = {
  width: 1.22,
  length: 2.44,
  thickness: 0.05,
  holeRadius: 0.152,
  holeFromBack: 0.458,
  rakeRise: 0.44,
  frontLift: 0.1,
} as const;

const BOARDS: Record<string, BoardAsset> = {
  "classic-oak": {
    id: "classic-oak",
    kind: "board",
    name: "Classic Oak",
    description: "Stock backyard board. Warm oak grain, cream stripe.",
    ...BOARD_GEOMETRY,
    wood: 0x8c5a2c,
    stripe: 0xfaf0c0,
    legWood: 0x3a200d,
  },
  "cherry-stain": {
    id: "cherry-stain",
    kind: "board",
    name: "Cherry Stain",
    description: "Deep red-brown cherry wood, gloss finish.",
    ...BOARD_GEOMETRY,
    wood: 0x6e2516,
    stripe: 0xf3d6b0,
    legWood: 0x2a0c08,
  },
  "painted-red": {
    id: "painted-red",
    kind: "board",
    name: "Painted Red",
    description: "Solid candy-apple paint with white stripe.",
    ...BOARD_GEOMETRY,
    wood: 0xc62828,
    stripe: 0xffffff,
    legWood: 0x6a0e0e,
  },
  "painted-blue": {
    id: "painted-blue",
    kind: "board",
    name: "Painted Blue",
    description: "Royal blue lacquer with gold stripe.",
    ...BOARD_GEOMETRY,
    wood: 0x1e3a8a,
    stripe: 0xf2c14e,
    legWood: 0x0c1a47,
  },
  "beach-white": {
    id: "beach-white",
    kind: "board",
    name: "Beach White",
    description: "Whitewashed planks. Beachhouse vibe.",
    ...BOARD_GEOMETRY,
    wood: 0xe8e3da,
    stripe: 0x6ab0d8,
    legWood: 0x8a7a64,
  },
  "black-steel": {
    id: "black-steel",
    kind: "board",
    name: "Black Steel",
    description: "Matte black deck, chrome stripe. Pro-shop look.",
    ...BOARD_GEOMETRY,
    wood: 0x1a1a1c,
    stripe: 0xb4b4b4,
    legWood: 0x0a0a0c,
  },
};

// All bags share regulation cloth dimensions — only the surface
// treatment varies. As with boards, the geometry constant spreads
// into each preset so the renderer stays uniform.
// Cornhole bag — 0.095 × 0.095 × 0.0125m (~3.7" × 3.7" × 0.5").
// Below regulation but reads visually correct because typical
// generated bag images have substantial inner margin around the
// bag content and the bbox UV maps the FULL image across the
// mesh — shrinking the mesh keeps the visible bag content snug
// against the silhouette.
const BAG_GEOMETRY = {
  size: 0.095,
  thickness: 0.0125,
  roughness: 0.95,
} as const;

// Bag seeds removed — admins generate their own bag assets through
// /admin/design and the merged catalog supplies them at runtime. We
// keep ONE "_default" safety bag (id-prefixed with "_") so the
// renderer can always mount even on an install with zero generated
// bags. The picker hides this entry via isUserVisibleAsset so it
// never shows up as a "choice" to the user — it's purely a fallback
// the validation effect snaps off of as soon as a real bag exists.
//
// Players & ghosts also point their default bagId at "_default";
// once the catalog loads, the game's persisted virtualTeam1Bag /
// virtualTeam2Bag override that anyway.
const BAGS: Record<string, BagAsset> = {
  _default: {
    id: "_default",
    kind: "bag",
    name: "Default bag",
    ...BAG_GEOMETRY,
    // Neutral fabric grey so a "default" bag visible to nobody but the
    // renderer doesn't accidentally suggest a brand color.
    color: 0x7d7d7d,
  },
};

const PLAYERS: Record<string, PlayerAsset> = {
  you: {
    id: "you",
    kind: "player",
    name: "You",
    // Pointed at the safety-fallback bag id. The game's persisted
    // virtualTeam1Bag overrides this at runtime when a real bag is
    // selected; this default only matters before the catalog has
    // resolved or on a fresh install with no generated bags.
    bagId: "_default",
  },
};

const GHOSTS: Record<string, GhostAsset> = {
  "ghost-2": {
    id: "ghost-2",
    kind: "ghost",
    name: "Ghost-2",
    bagId: "_default",
    tagline: "~2 pts/round · casual",
    averagePointsPerRound: 2,
    aimSigma: 0.55,
    powerMs: 1100,
    powerJitter: 380,
    loft: 0.55,
  },
  "ghost-3": {
    id: "ghost-3",
    kind: "ghost",
    name: "Ghost-3",
    bagId: "_default",
    tagline: "~3 pts/round · steady",
    averagePointsPerRound: 3,
    aimSigma: 0.32,
    powerMs: 1150,
    powerJitter: 230,
    loft: 0.5,
  },
  "ghost-4": {
    id: "ghost-4",
    kind: "ghost",
    name: "Ghost-4",
    bagId: "_default",
    tagline: "~4 pts/round · sharp",
    averagePointsPerRound: 4,
    aimSigma: 0.18,
    powerMs: 1180,
    powerJitter: 140,
    loft: 0.48,
  },
  "ghost-5": {
    id: "ghost-5",
    kind: "ghost",
    name: "Ghost-5",
    bagId: "_default",
    tagline: "~5 pts/round · ringer",
    averagePointsPerRound: 5,
    aimSigma: 0.09,
    powerMs: 1200,
    powerJitter: 80,
    loft: 0.46,
  },
};

// Built-in registry: minimal. Scenes, boards, AND bags keep ONE
// safety entry (id-prefixed with "_") so the renderer can ALWAYS
// mount, even before the catalog refetch lands or on installs
// with zero user-generated assets. Per-kind filters (see
// isUserVisibleAsset below) hide these "_"-prefixed fallbacks
// from the in-game pickers so the user only sees their own
// generated assets in the catalog.
export const BUILT_IN_REGISTRY: AssetRegistry = {
  scenes: {
    _default: {
      ...SCENES["backyard"]!,
      id: "_default",
      name: "Default scene",
    },
  },
  boards: { "classic-oak": BOARDS["classic-oak"]! },
  bags: BAGS,
  players: PLAYERS,
  ghosts: GHOSTS,
};

// Picker / validation filter — true if this asset id should appear
// in user-facing UI (scene picker, validation fallback). Renderer
// uses ALL registry entries; this only governs what the admin or
// player sees in the pickers.
export function isUserVisibleAsset(id: string): boolean {
  return !id.startsWith("_");
}
// Exported separately so the per-scene aligner can render the same
// reference board the game uses as its safety fallback.
export const REFERENCE_BOARD_TEMPLATE: BoardAsset = BOARDS["classic-oak"]!;

// ----- Lookup helpers -----

export function lookupScene(reg: AssetRegistry, id: string): SceneAsset {
  const s = reg.scenes[id];
  if (!s) throw new Error(`scene asset not found: ${id}`);
  return s;
}
export function lookupBoard(reg: AssetRegistry, id: string): BoardAsset {
  const b = reg.boards[id];
  if (!b) throw new Error(`board asset not found: ${id}`);
  return b;
}
export function lookupBag(reg: AssetRegistry, id: string): BagAsset {
  const b = reg.bags[id];
  if (!b) throw new Error(`bag asset not found: ${id}`);
  return b;
}
export function lookupPlayer(reg: AssetRegistry, id: string): PlayerAsset {
  const p = reg.players[id];
  if (!p) throw new Error(`player asset not found: ${id}`);
  return p;
}
export function lookupGhost(reg: AssetRegistry, id: string): GhostAsset {
  const g = reg.ghosts[id];
  if (!g) throw new Error(`ghost asset not found: ${id}`);
  return g;
}

// ----- Live catalog → registry merge -----

// Shape of the API row returned by GET /api/assets. Kept local so this
// file stays usable in isolation; lib/queries.ts has the same type.
export type CatalogRow = {
  id: string;
  // Includes "sfx"/"music" so this type accepts the full /api/assets
  // response; the merge below ignores those rows (sfx → lib/sfx-catalog,
  // music → the Scorer's MusicController, not the 3D registry).
  kind: "scene" | "board" | "bag" | "sfx" | "music";
  slug: string | null;
  name: string;
  description: string | null;
  imageUrl: string | null;
  swatchHex: number | null;
  metadata: Record<string, unknown>;
};

// Merge a catalog response into the built-in registry. DB rows replace
// any built-in entry with the same key (slug for built-ins, id
// otherwise). Players / ghosts are not user-editable yet, so they
// stay built-in.
//
// Tolerant of partial / malformed metadata — if a row is missing a
// required field for its kind, it's skipped (and logged via
// console.warn). The registry returned is always valid; worst case
// the user sees only the built-ins until the catalog is fixed.
export function mergeCatalogIntoRegistry(
  catalog: CatalogRow[],
  baseRegistry: AssetRegistry = BUILT_IN_REGISTRY,
): AssetRegistry {
  // Start with EMPTY maps and add catalog rows first; spread the
  // built-in fallback registry AFTER so its keys land later in the
  // map. Object.keys() returns insertion order, so the validation
  // effect's "first available" picks user-generated assets when
  // present, and only falls back to the built-in safety entry
  // (e.g. classic-oak board) when the catalog has nothing of that
  // kind. Without this reorder, the validation always snapped to
  // the built-in even when the user had a custom asset.
  const scenes: Record<string, SceneAsset> = {};
  const boards: Record<string, BoardAsset> = {};
  const bags: Record<string, BagAsset> = {};
  for (const row of catalog) {
    const key = row.slug ?? row.id;
    try {
      if (row.kind === "scene") {
        const meta = row.metadata as {
          palette?: SceneAsset["palette"];
          backdropDistanceZ?: number;
          backdropOffsetX?: number;
          backdropOffsetY?: number;
          backdropWidth?: number;
          backdropHeight?: number;
          backdropScale?: number;
          backdropOffsetU?: number;
          backdropOffsetV?: number;
        };
        if (!meta.palette) throw new Error("missing palette");
        scenes[key] = {
          id: key,
          kind: "scene",
          name: row.name,
          description: row.description ?? undefined,
          palette: meta.palette,
          skyImage: row.imageUrl ?? undefined,
          backdropDistanceZ: meta.backdropDistanceZ,
          backdropOffsetX: meta.backdropOffsetX,
          backdropOffsetY: meta.backdropOffsetY,
          backdropWidth: meta.backdropWidth,
          backdropHeight: meta.backdropHeight,
          backdropScale: meta.backdropScale,
          backdropOffsetU: meta.backdropOffsetU,
          backdropOffsetV: meta.backdropOffsetV,
        };
      } else if (row.kind === "board") {
        const meta = row.metadata as Partial<BoardAsset>;
        if (
          meta.width == null ||
          meta.length == null ||
          meta.thickness == null ||
          meta.holeRadius == null ||
          meta.holeFromBack == null ||
          meta.rakeRise == null ||
          meta.frontLift == null ||
          meta.wood == null ||
          meta.stripe == null ||
          meta.legWood == null
        ) {
          throw new Error("missing board geometry / tint");
        }
        boards[key] = {
          id: key,
          kind: "board",
          name: row.name,
          description: row.description ?? undefined,
          width: meta.width,
          length: meta.length,
          thickness: meta.thickness,
          holeRadius: meta.holeRadius,
          holeFromBack: meta.holeFromBack,
          rakeRise: meta.rakeRise,
          frontLift: meta.frontLift,
          wood: meta.wood,
          stripe: meta.stripe,
          legWood: meta.legWood,
          topImage: row.imageUrl ?? undefined,
        };
      } else if (row.kind === "bag") {
        const meta = row.metadata as Partial<BagAsset>;
        if (
          meta.color == null ||
          meta.size == null ||
          meta.thickness == null ||
          meta.roughness == null
        ) {
          throw new Error("missing bag dims / color");
        }
        bags[key] = {
          id: key,
          kind: "bag",
          name: row.name,
          color: meta.color,
          size: meta.size,
          thickness: meta.thickness,
          roughness: meta.roughness,
          topImage: row.imageUrl ?? undefined,
          // Optional texture transform. Reader falls back to identity
          // (0/0/1/0/false/false) when these are absent from metadata.
          texOffsetU: meta.texOffsetU,
          texOffsetV: meta.texOffsetV,
          texScale: meta.texScale,
          texRotationDeg: meta.texRotationDeg,
          texFlipU: meta.texFlipU,
          texFlipV: meta.texFlipV,
        };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(
        `Skipping malformed catalog row "${row.name}" (${row.kind}/${key}):`,
        e,
      );
    }
  }
  // Layer in built-in fallbacks AFTER catalog rows so user-generated
  // assets keep priority in Object.keys order. Only add a built-in
  // when its key isn't already in the map (catalog wins for any
  // slug collision).
  for (const [k, v] of Object.entries(baseRegistry.scenes)) {
    if (!scenes[k]) scenes[k] = v;
  }
  for (const [k, v] of Object.entries(baseRegistry.boards)) {
    if (!boards[k]) boards[k] = v;
  }
  for (const [k, v] of Object.entries(baseRegistry.bags)) {
    if (!bags[k]) bags[k] = v;
  }
  return {
    scenes,
    boards,
    bags,
    players: baseRegistry.players,
    ghosts: baseRegistry.ghosts,
  };
}

// ----- Loadout helpers -----

// Builds a sane loadout from a partial spec. Pulls bag defaults
// from the player/ghost asset's preferences when the caller didn't
// explicitly pick them.
export function buildLoadout(
  reg: AssetRegistry,
  opts: {
    sceneId: string;
    boardId?: string;
    team1: { playerId: string; bagId?: string };
    team2:
      | { playerId: string; bagId?: string }
      | { ghostId: string; bagId?: string };
  },
): GameLoadout {
  const boardId = opts.boardId ?? Object.keys(reg.boards)[0]!;
  const t1Player = lookupPlayer(reg, opts.team1.playerId);
  let team2: TeamLoadout;
  if ("ghostId" in opts.team2) {
    const g = lookupGhost(reg, opts.team2.ghostId);
    team2 = {
      ghostId: g.id,
      bagId: opts.team2.bagId ?? g.bagId,
    };
  } else {
    const p = lookupPlayer(reg, opts.team2.playerId);
    team2 = {
      playerId: p.id,
      bagId: opts.team2.bagId ?? p.bagId,
    };
  }
  return {
    sceneId: opts.sceneId,
    boardId,
    team1: {
      playerId: t1Player.id,
      bagId: opts.team1.bagId ?? t1Player.bagId,
    },
    team2,
  };
}
